import { Group, Vector3, Object3D, Mesh, Scene } from "three";
import { ClickMesh } from "./ClickMesh";
import { Hotspot } from "./Hotspot";


const getName = function (mesh:Object3D):string {
  const name = mesh.name; 

  if (name === null) {
    return "Unknown Name";
  }

  const buildingName = (name:string): string => {
    const split = name.split("_");
    if (parseInt(split[1]) > 30) return "Kattenbrookstrift " + split[1];
    return "Mailänder Str. " + split[1];
  }

  const levelName = (name: string): string => {
    const myArray = name.split("_");
    const a = parseInt(myArray[0])
    const b = parseInt(myArray[2])
    if (b == 0) return "Erdgeschoss";
    return b + ". OG";
  }

  const apartmentName = (name:string):string => {
    const split = name.split("_");
    const apartmentNumber = parseInt(split[2]);
    const apartmentIdentifier = parseInt(split[3]);
    if (Number.isNaN(apartmentNumber)) return "Wohnung betreten";

    let apartmentName = "Unbennante Wohnung"; //Fallback

    if (apartmentIdentifier === 10) apartmentName = "Linke Wohnung";
    else if (apartmentIdentifier === 11) apartmentName = "1. Linke Wohnung";
    else if (apartmentIdentifier === 12) apartmentName = "2. Linke Wohnung";

    else if (apartmentIdentifier === 20) apartmentName = "Rechte Wohnung";
    else if (apartmentIdentifier === 21) apartmentName = "1. Rechte Wohnung";
    else if (apartmentIdentifier === 22) apartmentName = "2. Rechte Wohnung";

    else if (apartmentIdentifier === 30) apartmentName = "Mittige Wohnung";

    else if (apartmentIdentifier === 40) apartmentName = "Gerade Wohnung";

    else if (apartmentIdentifier === 50) apartmentName = "Wohnung";

    return apartmentName;
  }

  if (name.toLowerCase().includes("building")) {
    return buildingName(name);
  }
  if (name.toLowerCase().includes("level")) {
    return levelName(name);
  }
  if (name.toLowerCase().includes("hotspot")) {
    return hotspotName(name);
  }
  if (!/[a-zA-Z]/.test(name)) { //apartment
    return apartmentName(name);
  }
  
  return name;
}

const hotspotName = (name:string):string => {
  const a = name.split("_")[2].replace(/[0-9]/g, '');
  switch (a) {
    case "bath":
      return "Badezimmer"
    case "hall":
      return "Flur"
    case "kitchen":
      return "Küche"
    case "living":
      return "Wohnzimmer"
    case "room":
      return "Schlafzimmer"
    default:
      return "Raum";
  }
}

export class TreeNode {
  private _parent:TreeNode;
  private _children:Array<TreeNode>;
  private _clickables:Array<Hotspot>;
  private _clickMesh:ClickMesh;

  constructor(parent:TreeNode, clickMesh:ClickMesh) {
    this._parent = parent;
    this._children = new Array<TreeNode>();
    this._clickables = new Array<Hotspot>();
    this._clickMesh = clickMesh;
  }

  addChildren(node: TreeNode | Array<TreeNode>) {
    if (node === null) return;
    if (Array.isArray(node)) {
      this.children.push(...node);
    } else {
      this.children.push(node);
    }
  }

  addClickable(node: Hotspot | Array<Hotspot>) {
    if (node == null) return;
    if (Array.isArray(node)) {
      this.clickables.push(...node);
    } else {
      this.clickables.push(node);
    }
  }

  get children(): Array<TreeNode> {
    return this._children;
  }

  get clickables():Array<Hotspot> {
    return this._clickables;
  }

  set clickables(clickables:Array<Hotspot>) {
    this._clickables = clickables;
  }

  get parent():TreeNode {
    return this._parent;
  }

  get clickMesh():ClickMesh {
    return this._clickMesh;
  }

}

export class Tree {
  private _cursor:TreeNode;
  private _root:TreeNode;
  private _level:number;
  private _scene:Scene;
  private _activeHotspots:Array<Hotspot>;
  private _selectedId:string;

  get cursor():TreeNode {
    return this._cursor;
  }

  get root():TreeNode {
    return this._root;
  }

  get level(): number {
    return this._level;
  }

  get activeButtons():Array<any> {
    const activeButtons = this._cursor.children.map(c => { return { title:getName(c.clickMesh.getMeshParent()), id: c.clickMesh.id}; });

    if (this._level <= 0) {
      const desiredOrder = [
        "Kattenbrookstrift 137",
        "Kattenbrookstrift 139",
        "Kattenbrookstrift 141",
        "Kattenbrookstrift 143",
        "Kattenbrookstrift 145",
        "Kattenbrookstrift 147",
        "Mailänder Str. 23",
        "Mailänder Str. 25",
        "Kattenbrookstrift 151",
        "Kattenbrookstrift 149",
      ];

      activeButtons.sort((a, b) => {
        const aTargetIndex = desiredOrder.findIndex((el) => el === a.title);
        const bTargetIndex = desiredOrder.findIndex((el) => el === b.title);
        return aTargetIndex - bTargetIndex;
      })
    }
    

    return activeButtons;
  }

  get selectedId():string {
    return this._selectedId;
  }

  constructor(scene:Scene) {
    this._scene = scene;
    this._root = new TreeNode(null, new ClickMesh(new Object3D()));
    this._cursor = this._root;
    this._level = -1;
    this._activeHotspots = new Array<Hotspot>();

    const realRoot = scene.getObjectByName("threngine_origins").getObjectByName("9gx2me4z");

    const buildings = realRoot.children.filter(b => b.name.toLowerCase().includes("building"));
    let parent = this._root;
    buildings.forEach(b => {
      const collider = b.children.find(e => e.name.includes("Collider"));
      const node = new TreeNode(parent, new ClickMesh(collider));
      parent.addChildren(node);
      parent = node;
      const levels = b.children.filter(l => l.name.toLowerCase().includes("level"));
      levels.forEach(l => {
        const levelCollider = l.children.find(c => c.name.toLowerCase().includes("collider"));
        const node = new TreeNode(parent, new ClickMesh(levelCollider));
        parent.addChildren(node);
        parent = node;
        const flats = l.children;
        flats.forEach(f => {
          const flatCollider = f.children.find(c => c.name.toLowerCase().includes("collider"));
          if (flatCollider) {
            const node = new TreeNode(parent, new ClickMesh(flatCollider));
            parent.addChildren(node);
            parent = node;
            //f.children.forEach(fl=>{
            const flatCollider2 = flatCollider.clone();
            //flatCollider2.position.copy(new Vector3(0,0,0));
            flatCollider2.name = flatCollider.name + "_helper";
            if (flatCollider.children.length == 0)
              flatCollider.parent.add(new Group().add(flatCollider2));
            const subNode = new TreeNode(parent, new ClickMesh(flatCollider2));
            parent.addChildren(subNode);
            //})
            parent = parent.parent;
          }
        })
        parent = parent.parent;

      })
      parent = parent.parent;
    })

    console.error("ROOT", this._root);
  }

  private _setCursor(cursor:TreeNode) {
    // console.log("_setCursor() called!", this.level);
    let level = 0;
    let selection = cursor;
    while (selection.parent != null) {
      selection = selection.parent;
      level++;
    }
    this._level = level;
    this._cursor = cursor;
  }

  public getLevelDesc = ():Array<string> => {
    const desc = [];

    switch (this._level) {
      case 0:
        desc.push("Gebäudeauswahl");
        break;
      case 1: {
        const buildingMesh = this._cursor.clickMesh.getMeshParent();
        desc.push("Gebäude: " + getName(buildingMesh));
        break;
      }
      case 2: {
        const levelMesh = this._cursor.clickMesh.getMeshParent();
        const buildingMesh = levelMesh.parent;
        desc.push("Gebäude: " + getName(buildingMesh));
        desc.push("Geschoss: " + getName(levelMesh));
        break;
      }
      case 3: {
        const apartmentMesh = this._cursor.clickMesh.getMeshParent();
        const levelMesh = apartmentMesh.parent;
        const buildingMesh = levelMesh.parent;
        desc.push("Gebäude: " + getName(buildingMesh));
        desc.push("Geschoss: " + getName(levelMesh));
        desc.push("Wohnung: " + getName(apartmentMesh));
        break;
      }
      case 4: {
        const roomHotspotMesh = this.selectMesh(this._selectedId);
        const roomMesh = this._cursor.clickMesh.getMeshParent();
        const apartmentMesh = roomMesh.parent;
        const levelMesh = apartmentMesh.parent;
        const buildingMesh = levelMesh.parent;
        desc.push("Gebäude: " + getName(buildingMesh));
        desc.push("Geschoss: " + getName(levelMesh));
        desc.push("Wohnung: " + getName(apartmentMesh));
        desc.push("Raum: " + hotspotName(roomHotspotMesh.name));
        break;
      }
      default: {
        desc.push("Gebäudeauswahl");
        break;
      }
    }

    return desc;
  }

  public createRoundTourHotspots = () => {
    const exteriorRoot = this._scene.getObjectByName("threngine_origins").getObjectByName("wncu4s0j");
    exteriorRoot.children.forEach(mesh => {
      if (mesh.name.toLowerCase().includes("hotspot")) {
        const h = new Hotspot(mesh, 2);
        this.cursor.addClickable(h)
        this._activeHotspots.push(h);
        this._scene.add(h.getMesh());
        h.getMesh().visible = false;
      }
    });
  }

  //TODO: call on startup (to jump to hotspot instead of default bird eye view camera)
  selectNodeById(id:string) {
    console.log("Tree.selectNodeById() called!");

    this._selectedId = id;

    this.traverse(this._root, (nodeN) => {
      if (nodeN.clickMesh?.id === id) this._setCursor(nodeN);
    })

    console.error("LEVEL: ", this.level);
    switch (this.level) {
      case 0: //in the exterior
        if (this.cursor.clickables.length == 0) { //enter level 0
          this.createRoundTourHotspots();
          return this._selectedId = this.cursor.clickables[0].id;
        }
        else { //stay in level 0
          this.cursor.clickables.forEach(h => h.getMesh().visible = true)
          this.cursor.clickables.find(h => h.id === id).getMesh().visible = false;
        }
        break;

      case 1: { //around a building
        //FIXME: TODO: Refactor!

        if (this.cursor.parent.clickables.length > 0) { //remove clickables if coming from level 0
          this.cursor.parent.clickables.forEach(c => {
            this._scene.remove(c.getMesh())
          })
          this.cursor.parent.clickables = [];
          this._activeHotspots = [];
        }

        //hide inner stuff of buildings
        this.cursor.clickMesh.getMeshParent().children.forEach(levelMesh => {
          if (levelMesh.name.toLowerCase().includes("roof")) levelMesh.visible = true;
          if (levelMesh.name.toLowerCase().includes("level")) {
            levelMesh.visible = true;
            levelMesh.children.forEach(apartmentMesh => {
              if (apartmentMesh.name.toLowerCase().includes("outside")) apartmentMesh.visible = true;
              if (apartmentMesh.name.toLowerCase().includes("windows")) apartmentMesh.visible = true;
              if (apartmentMesh.name.toLowerCase().includes("ceiling")) apartmentMesh.visible = false;
              if (apartmentMesh.name.toLowerCase().includes("staircase")) apartmentMesh.visible = false;
              if (apartmentMesh.name.toLowerCase().includes("fill")) apartmentMesh.visible = false;
              if (!/[a-zA-Z]/.test(apartmentMesh.name)) apartmentMesh.visible = false; //actual apartment
            })
          }
        });
        break;
      }

      case 2: { //floor overview
        const floorNode =  this.cursor;
        const buildingNode = floorNode.parent;

        this.cursor.parent.clickMesh.getMeshParent().children.forEach(level => { //hide Rooftop
          if (level.name.toLowerCase().includes("roof"))
            level.visible = false;
        });

        //SHOW FLATS OF SELECTED LEVEL
        this.cursor.clickMesh.getMeshParent().children.forEach(flat => {
          flat.traverse((obj) => {
            if (obj.name.toLowerCase().includes("collider") || obj.name.toLowerCase().includes("ceiling") || obj.name.toLowerCase().includes("light") || obj.name.toLowerCase().includes("measure"))
              obj.visible = false;
            else
              obj.visible = true;
          })
        });

        //Handle other levels 
        const selectedFloor = parseInt(floorNode.clickMesh.getMeshParent().name.split("_")[2]);
        buildingNode.clickMesh.getMeshParent().children.forEach(levelMesh => {
          if (levelMesh.name.toLowerCase().includes("level")) {
            const floor = parseInt(levelMesh.name.split("_")[2]);

            if (floor <= selectedFloor) {
              levelMesh.visible = true;
              levelMesh.children.forEach((el) => {
                if (el.name.toLowerCase().includes("staircase")) {
                  el.traverse((el) => {
                    el.visible = true;
                  })
                }
              })
            } else {
              levelMesh.visible = false;
            }
          }
        });
        break;
      }

      case 3: //Aparment birds eye view
        console.log("Cursor", this.cursor);

        const floorNode =  this.cursor.parent;
        const buildingNode = floorNode.parent;

        //Hide/ unhide stuff
        this.furnitureVisible = true;
        this.doorsVisible = true;
        this.measurementsVisible = false;

        this.cursor.clickMesh.getMeshParent().traverse(mesh => {
          if (mesh.name.toLowerCase().includes("collider") || mesh.name.toLowerCase().includes("ceiling") || mesh.name.toLowerCase().includes("light") || mesh.name.toLowerCase().includes("measure"))
            mesh.visible = false;
          if (mesh.name.toLowerCase().includes("furniture") || (mesh.name.toLowerCase().includes("door") && mesh.name.toLowerCase().includes("inner"))) {
            mesh.visible = true;
          }
        });

        //Hide roof
        buildingNode.clickMesh.getMeshParent().children.forEach((el) => {
          if (el.name.toLowerCase().includes("roof")) el.visible = false;
        });

        //Remove hotspots from children
        if (this._activeHotspots.length > 0) { //Remove clickables from if coming from level 0
          this.cursor.children.forEach(c => {
            c.clickables.forEach(cl => {
              this._scene.remove(cl.getMesh())
            })
            c.clickables = [];
          })
          this._activeHotspots = [];
        }

        //Hide ceilings
        this.cursor.parent.clickMesh.getMeshParent().traverse((mesh) => {
          if (mesh.name.toLowerCase().includes("ceiling")) {
            mesh.visible = false;
          }
        })

        //Handle other levels 
        const selectedFloor = parseInt(floorNode.clickMesh.getMeshParent().name.split("_")[2]);
        buildingNode.clickMesh.getMeshParent().children.forEach(levelMesh => {
          if (levelMesh.name.toLowerCase().includes("level")) {
            const floor = parseInt(levelMesh.name.split("_")[2]);

            if (floor <= selectedFloor) {
              levelMesh.visible = true;
              levelMesh.children.forEach((el) => {
                if (el.name.toLowerCase().includes("staircase")) {
                  el.traverse((el) => {
                    el.visible = true;
                  })
                }
              })
            } else {
              levelMesh.visible = false;
            }
          }
        });

        break;

      case 4: { //inside apartment
        console.log("Cursor", this.cursor);

        const apartmentNode = this.cursor.parent;
        const floorNode =  apartmentNode.parent;
        const buildingNode = floorNode.parent;

        apartmentNode.clickMesh.getMeshParent().traverse((el) => {
          if (el.type === "PointLight") {
              console.log(el);
              el.visible = true;
              if (el.parent) el.parent.visible = true;
              (el as THREE.PointLight).intensity = 0;
          }
        })

        //Hide roof if on floor 3
        const floor = parseInt(floorNode.clickMesh.name.split("_")[2]);
        if (floor === 3) {
          buildingNode.clickMesh.getMeshParent().children.forEach((el) => {
            if (el.name.toLowerCase().includes("roof")) el.visible = true;
          });
        }

        //Unhide ceiling
        floorNode.clickMesh.getMeshParent().traverse(mesh => {
          if (mesh.name.toLowerCase().includes("ceiling") || mesh.name.toLowerCase().includes("outside")) {
            mesh.visible = true;
          }
        });

        //Handle outside (balconies) of higher floors
        const selectedFloor = parseInt(floorNode.clickMesh.getMeshParent().name.split("_")[2]);
        buildingNode.clickMesh.getMeshParent().children.forEach(levelMesh => {
          if (levelMesh.name.toLowerCase().includes("level")) {
            const floor = parseInt(levelMesh.name.split("_")[2]);

            // console.log(floor, selectedFloor);

            if (floor >= selectedFloor) {
              levelMesh.visible = true;
              levelMesh.children.forEach((el) => {
                if (el.name.toLowerCase().includes("outside")) {
                  el.traverse((el) => {
                    el.visible = true;
                  })
                }
              })
            }
          }
        });

        if (this.cursor.clickables.length == 0) { //entering level 4 from a different level
          this.cursor.parent.clickMesh.getMeshParent().children.forEach(flatChildren => {
            if (flatChildren.name.toLowerCase().includes("hotspot")) {
              console.error("Adding hotspot to ", flatChildren.name)
              const h = new Hotspot(flatChildren, 0.25);
              this.cursor.addClickable(h)
              this._activeHotspots.push(h);
              this._scene.add(h.getMesh());
            }
          });

          this.doorsVisible = false;
          this.setDoorVisibility(this.cursor.parent.clickMesh.getMeshParent(), this.doorsVisible);

          this.cursor.clickables.find(c => c.name.includes("living")).getMesh().visible = false;
          return this._selectedId = this.cursor.clickables.find(c => c.name.includes("living")).id;
        }

        else { //staying in level 4
          this.cursor.clickables.forEach(h => h.getMesh().visible = true)
          this.cursor.clickables.find(h => h.id === id).getMesh().visible = false;
        }

        break;
      }

      default: {
        throw new Error("R U trying to enter an unkown Level?! ^.-")
        break;
      }
    }
    return id;
  }

  handleLights = (id:string) => {
    //Needs to be very smooth to look good - performance... ?
    const LIGHT_INTENSITY_INTERVAL = 13;
    const LIGHT_INTENSITY_STEP = 0.5;

    console.error("HANDLE LIGHTS");
    const selectedHS = this.cursor.clickables.find(h => h.id == id);
    if (selectedHS == null || this.level != 4) return;
    const hotspotNameArray = selectedHS.name.split("_");
    let hotspotName = hotspotNameArray[1].replace(/[0-9]/g, '');
    if (hotspotNameArray[2] != null) { //bad naming conventions from graphics side
      hotspotName += "_" + hotspotNameArray[2].replace(/[0-9]/g, '');
    }
    console.error("LIGHT: SELECTEDHS: ", hotspotName);
    this.cursor.parent.clickMesh.getMeshParent().traverse(el => {
      if (el.type === "PointLight") {
        let light = el as THREE.PointLight;
        el.castShadow = false;

        if (light.name.includes(hotspotName)) {
          if (!light.userData.isOn) {
            light.userData.isOn = true;
            let intensityPercentage = 0;
            let lightInterval = window.setInterval(() => {
              if (intensityPercentage >= 100 || !light.userData.isOn) { //light.userData.isOn exists because we need a clean way to stop these intervals from outside
                window.clearInterval(lightInterval);
              }
              intensityPercentage += LIGHT_INTENSITY_STEP;
              light.intensity = light.userData.intensity * (intensityPercentage/100);
            }, LIGHT_INTENSITY_INTERVAL)
          }
          console.warn(light.name, light.intensity);
        } else {
          if (light.userData.isOn) {
            light.userData.isOn = false;
            let intensityPercentage = 100;
            let lightInterval = window.setInterval(() => {
              if (intensityPercentage <= 0 || light.userData.isOn) { //light.userData.isOn exists because we need a clean way to stop these intervals from outside
                window.clearInterval(lightInterval);
              }
              intensityPercentage -= LIGHT_INTENSITY_STEP;
              light.intensity = light.userData.intensity * (intensityPercentage/100);
            }, LIGHT_INTENSITY_INTERVAL)
          }
        }
        
      }
    })
  }


  traverse(node:TreeNode, callback = (node:TreeNode):void => { }) {
    if (node != null) {
      callback(node);
      node.children.forEach(item => this.traverse(item, callback));
    }
  }

  selectMesh(id:string):Mesh {
    let mesh = undefined;
    this.traverse(this._root, (nodeN) => {
      if (nodeN.clickMesh?.id === id)
        mesh = nodeN.clickMesh.getMesh();
    })
    this.cursor.clickables.forEach(item => {
      if (item.id === id) {
        mesh = item.getMesh();
      }
    })
    if (mesh == null) {
      throw new Error("Mesh could not be found!");
    }
    return mesh as Mesh;
  }

  updateHotspots(camPos: Vector3) {
    this._activeHotspots.forEach(h => h.update(camPos));
  }

  getClickMeshes() {
    return [
      ...this.cursor.children.
        map(c => c.clickMesh.getMesh()),
      ...this.cursor.clickables.
        filter(c => c.id != this._selectedId).
        map(c => c.getMesh())
    ]
  }

  /*
    Logically all of this visibility stuff should be part of Controller rather than Tree, but we'll have to put that off until the next major refactor
  */
  public measurementsVisible:boolean = false;
  public doorsVisible:boolean = false;
  public furnitureVisible:boolean = true;

  public setMeasurementVisibility = (mesh:Object3D, visible:boolean) => {
    mesh.traverse((el) => {
      if (el.name.toLowerCase().includes("measure")) {
        el.visible = visible;
      }
    })
  }

  public setDoorVisibility = (mesh:Object3D, visible:boolean) => {
    mesh.traverse((el) => {
      if (el.name.toLowerCase().includes("door") && el.name.toLowerCase().includes("inner")) {
        el.visible = visible;
      }
    })
  }

  public setFurnitureVisibility = (mesh:Object3D, visible:boolean) => {
    mesh.traverse((el) => {
      if (el.name.toLowerCase().includes("furniture")) {
        el.visible = visible;
      }
    })
  }

  public setActiveHotspotsVisibility = (visible:boolean) => {
    this._activeHotspots.forEach((el) => {
      el.getMesh().visible = visible;
    })
  }
}
